import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Una lettura che si annulla quando ne parte un'altra.
 *
 * Undici schermate ripetevano le stesse otto righe — uno stato, un `useCallback`
 * con `try/catch`, un `useEffect` che lo chiama — e nessuna delle undici aveva
 * un `AbortController`. Con una rete lenta e un clic veloce due richieste
 * tornano invertite e la schermata mostra i dati del filtro **precedente**: un
 * registro accessi che dice "ultimi 7 giorni" mentre sotto ci sono novanta
 * giorni di righe non è un ritardo, è una risposta sbagliata a una domanda
 * precisa.
 *
 * Copre le letture che **rileggono a un filtro**: una richiesta, un risultato a
 * schermo, un errore.
 *
 * Restano fuori quelle di forma diversa, e vale la pena dire quale forma perché
 * "diversa" senza il resto è un debito travestito da decisione:
 *
 *  - quella degli **orari** semina un modulo che poi si modifica a mano, e
 *    quella delle **soglie di supervisione** riempie quattro pezzi di stato da
 *    una risposta sola: `useLoad` tiene un valore, non quattro, e non sa niente
 *    di un modulo che diverge da ciò che ha letto;
 *  - quella del **thread di supervisione** e quella della **console** girano a
 *    intervallo finché la schermata è aperta: lì non c'è nessun filtro che
 *    cambi, e l'annullamento della richiesta precedente non vuol dire niente.
 *
 * Nessuna delle due forme guadagnerebbe qualcosa a passare di qui (AO1).
 *
 * Qui la richiesta di prima si annulla, e comunque il risultato di una lettura
 * annullata non tocca niente: sono due difese, e la seconda regge anche quando
 * chi chiama non passa il segnale a `fetch`.
 *
 * `load` deve essere **stabile** (`useCallback` con i filtri fra le dipendenze):
 * è quello a decidere quando si rilegge, esattamente come una lista di
 * dipendenze. Una funzione anonima rileggerebbe all'infinito
 * (`docs/DECISIONI.md`, AL1).
 */

export type LoadState<T> = {
  /** `undefined` finché la prima lettura non è tornata: è lo stato "caricamento". */
  data: T | undefined;
  /**
   * L'ultima lettura è fallita.
   *
   * Senza, una schermata mostrava "Caricamento…" **accanto** al messaggio
   * d'errore, per sempre: due affermazioni che si contraddicono nella stessa
   * pagina, e la seconda letta da chi non vede solo se torna indietro con lo
   * screen reader.
   */
  failed: boolean;
  /** Rilegge adesso, annullando quella in corso. */
  reload: () => Promise<void>;
};

export type LoadOptions<T> = {
  /** Dove finisce l'errore. Non viene chiamato per una lettura annullata. */
  onError: (error: unknown) => void;
  /** Chiamato solo per la lettura **corrente**: mai per una tornata in ritardo. */
  onLoaded?: (value: T) => void;
};

/** `fetch` annullata: non è un guasto e non si mostra a nessuno. */
function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export function useLoad<T>(
  load: (signal: AbortSignal) => Promise<T>,
  options: LoadOptions<T>,
): LoadState<T> {
  const [state, setState] = useState<{ data: T | undefined; failed: boolean }>({
    data: undefined,
    failed: false,
  });
  const running = useRef<AbortController>(undefined);

  /**
   * Le funzioni di chi chiama in un riferimento, aggiornato **dopo** il disegno.
   *
   * Nelle dipendenze non possono stare: sono quasi sempre scritte nel corpo del
   * componente, quindi nuove a ogni disegno, e farebbero ripartire la lettura
   * all'infinito (AL1). Scriverle durante il disegno funzionerebbe, ma è la
   * cosa che in modalità concorrente non è garantita: l'effetto costa una riga.
   */
  const handlers = useRef(options);
  useEffect(() => {
    handlers.current = options;
  });

  const run = useCallback(async () => {
    running.current?.abort();
    const current = new AbortController();
    running.current = current;

    try {
      const next = await load(current.signal);
      if (current.signal.aborted) return;
      setState({ data: next, failed: false });
      handlers.current.onLoaded?.(next);
    } catch (error) {
      if (current.signal.aborted || isAbort(error)) return;
      setState((previous) => ({ data: previous.data, failed: true }));
      handlers.current.onError(error);
    }
  }, [load]);

  useEffect(() => {
    void run();
    /**
     * Smontando — o cambiando filtro — la richiesta in volo si annulla.
     *
     * Serve a due cose: la risposta non arriva più a un componente che non
     * c'è, e la connessione si libera invece di restare appesa a una schermata
     * che nessuno sta guardando.
     */
    return () => running.current?.abort();
  }, [run]);

  return { data: state.data, failed: state.failed, reload: run };
}
